import { PublicLayout } from '@/components/public/PublicLayout';
import { PublicSeo } from '@/components/public/PublicSeo';
import { motion } from '@/components/public/PublicMotion';
import { fadeInUp, scaleIn, staggerContainer } from '@/hooks/use-scroll-animation';
import { Card, CardContent } from '@/components/ui/card';
import { Heading } from '@/components/ui/heading';

const founders = [
  {
    name: 'Ing. Daniele Di Bernardo',
    role: 'Co-founder',
    photo: 'https://cdn.emovia.com/img/team/daniele.webp',
    bio: "CTO con oltre 15 anni di esperienza nello sviluppo di piattaforme digitali. Ha guidato l'innovazione tecnologica in settori complessi, dall'edtech al fashion-tech, con competenze in architetture cloud-native e sviluppo full-stack.",
  },
  {
    name: 'Dott.ssa Elisa Manca',
    role: 'Psicologa, Co-founder',
    photo: 'https://cdn.emovia.com/img/team/elisa.webp',
    bio: 'Psicologa con master, co-founder di Emovia. Ha progettato il modello clinico alla base della piattaforma.',
  },
];

const ChiSiamo = () => {
  return (
    <PublicLayout>
      <PublicSeo
        title="Emovia | Chi Siamo"
        description="Scopri il team dietro Emovia: professionisti della psicologia e della tecnologia uniti per migliorare il lavoro clinico tra le sedute."
        path="/chi-siamo"
      />

      {/* Hero emozionale — sfondo scuro, full-width */}
      <section className="relative min-h-[40vh] bg-primary-dark overflow-hidden flex items-center py-24 md:py-32">
        {/* Decorazioni geometriche animate */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <motion.div
            animate={{ y: [0, 20, 0] }}
            transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/[0.03] md:h-[28rem] md:w-[28rem]"
          />
          <motion.div
            animate={{ y: [0, -16, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
            className="absolute -left-16 bottom-0 h-64 w-64 rounded-full bg-white/[0.03] md:h-80 md:w-80"
          />
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 5 }}
            className="absolute left-1/2 -top-12 h-48 w-48 -translate-x-1/2 rounded-full bg-white/[0.02]"
          />
        </div>

        <div className="container relative">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="mx-auto max-w-3xl text-center"
          >
            <motion.p
              variants={fadeInUp}
              transition={{ duration: 0.4 }}
              className="mb-6 text-sm uppercase tracking-[0.18em] text-blue-lighter/70"
            >
              Il team
            </motion.p>

            <motion.div variants={fadeInUp} transition={{ duration: 0.5 }}>
              <Heading level={1} font="display" className="text-white">
                Clinica e codice, stesso tavolo.
              </Heading>
            </motion.div>

            <motion.p
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="mt-6 text-lg text-white/90"
            >
              Emovia nasce dall&apos;incontro tra una psicologa e un ingegnere. Non per
              digitalizzare la terapia, ma per dare al professionista strumenti migliori.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Sezione founder — sfondo chiaro */}
      <section className="py-20 md:py-28">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            variants={staggerContainer}
            className="mx-auto grid max-w-4xl gap-8 md:grid-cols-2"
          >
            {founders.map((founder, index) => (
              <motion.div
                key={founder.name}
                variants={fadeInUp}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <Card className="group h-full overflow-hidden border-border bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <CardContent className="p-8">
                    <div className="flex flex-col items-center gap-6 text-center">
                      {/* Foto con ring decorativo + float + hover scale */}
                      <motion.div
                        variants={scaleIn}
                        transition={{ duration: 0.4, delay: 0.1 + index * 0.15 }}
                        className="relative shrink-0"
                      >
                        <motion.div
                          animate={{ y: [0, -5, 0] }}
                          transition={{ duration: index === 0 ? 6 : 7, repeat: Infinity, ease: 'easeInOut', delay: index * 0.5 }}
                        >
                          <img
                            src={founder.photo}
                            alt={founder.name}
                            className="h-40 w-40 rounded-3xl object-cover ring-4 ring-primary/10 transition-all duration-300 group-hover:scale-[1.03] group-hover:ring-primary/20 md:h-48 md:w-48"
                            width={192}
                            height={192}
                          />
                        </motion.div>
                      </motion.div>

                      {/* Nome e ruolo (ruolo con fade ritardato) */}
                      <div className="space-y-1">
                        <Heading level={3} className="text-primary">
                          {founder.name}
                        </Heading>
                        <motion.p
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: 0.2 + index * 0.15 }}
                          className="text-sm text-muted-foreground"
                        >
                          {founder.role}
                        </motion.p>
                      </div>

                      {/* Separatore con reveal dal centro */}
                      <motion.div
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.3 + index * 0.15 }}
                        className="h-px w-10 origin-center bg-primary/20"
                      />

                      {/* Bio con fade ritardato */}
                      <motion.p
                        initial={{ opacity: 0, y: 8 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.45 + index * 0.15 }}
                        className="leading-relaxed text-primary-dark"
                      >
                        {founder.bio}
                      </motion.p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default ChiSiamo;
